import {
  DefaultError,
  QueryClient,
  queryOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import {
  KentRebalancingPlan,
  KentRebalancingPlanStatusEnum,
} from '@cast/types';

import { KENT_REBALANCING_IN_PROGRESS_STATES } from 'constants/cluster';
import { apiClient } from '@cast/infrastructure-api';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

import { getKentRebalancePlanQueryKey } from './useKentRebalancePlansQuery';

type UseKentRebalancePlanQuery = {
  isSuccess: boolean;
  isLoading: boolean;
  isError: boolean;
  rebalancePlan?: KentRebalancingPlan;
  error?: DefaultError;
  refetch: () => void;
};

type UseKentRebalancePlanQueryProps = {
  clusterId?: string;
  rebalancePlanId?: string;
  enabled?: boolean;
  meta?: Record<string, unknown>;
  refetchInterval?: number | false;
};

const getKentRebalancePlanQueryOptions = ({
  clusterId,
  rebalancePlanId,
  organizationId,
  enabled = true,
  meta,
  refetchInterval,
  client,
}: UseKentRebalancePlanQueryProps & {
  organizationId?: string;
  client: QueryClient;
}) =>
  queryOptions({
    queryKey: getKentRebalancePlanQueryKey(clusterId, rebalancePlanId),
    queryFn: async () => {
      const { data } = await apiClient.kent.getRebalancingPlan({
        organizationId: organizationId ?? '',
        clusterId: clusterId!,
        id: rebalancePlanId!,
      });

      return data || null;
    },
    enabled: enabled && !!clusterId && !!rebalancePlanId && !!organizationId,
    refetchInterval: ({ queryKey }) => {
      if (refetchInterval) {
        return refetchInterval;
      }

      const data = client.getQueryData<any>(queryKey);
      if (
        data &&
        ![
          KentRebalancingPlanStatusEnum.GENERATING,
          ...KENT_REBALANCING_IN_PROGRESS_STATES,
        ].includes(data.status)
      ) {
        return false;
      }

      return 5000;
    },
    meta,
  });

export const useKentRebalancePlanQuery = ({
  clusterId,
  rebalancePlanId,
  enabled = true,
  meta,
  refetchInterval,
}: UseKentRebalancePlanQueryProps): UseKentRebalancePlanQuery => {
  const client = useQueryClient();
  const { currentOrganization } = useOrganizations();

  const {
    isLoading,
    isError,
    data: rebalancePlan,
    error,
    refetch,
    isSuccess,
  } = useQuery(
    getKentRebalancePlanQueryOptions({
      clusterId,
      rebalancePlanId,
      organizationId: currentOrganization?.id,
      enabled,
      meta,
      refetchInterval,
      client,
    })
  );

  return {
    isError,
    isSuccess,
    isLoading,
    rebalancePlan,
    error: error || undefined,
    refetch,
  };
};
